import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import GuestLayout from "@/components/guest/GuestLayout.jsx";
import ServiceRequestForm from "@/components/guest/ServiceRequestForm.jsx";
import { useSession } from "@/hooks/useSession.js";

import { ChevronRight, ConciergeBell, Info, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  guestRequestsApi,
} from "@/api/guest/guestRoomServicesApi";
import { guestHotelServicesApi } from "@/api/guest/guestHotelServicesApi";

const getApiData = (response) => {
  return response?.data?.data || response?.data?.Data || response?.data || null;
};

const getPagedItems = (response) => {
  const data = getApiData(response);

  return (
    data?.items ||
    data?.Items ||
    data?.data ||
    data?.Data ||
    (Array.isArray(data) ? data : [])
  );
};

const getApiMessage = (error, fallback = "Something went wrong.") => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data?.title ||
    fallback
  );
};

const inferAmenityIcon = (name = "") => {
  const value = name.toLowerCase();

  if (value.includes("water") || value.includes("drink")) return "💧";
  if (value.includes("clean") || value.includes("housekeep")) return "🧹";
  if (value.includes("food") || value.includes("room service") || value.includes("dining") || value.includes("meal")) return "🍽️";
  if (value.includes("bar") || value.includes("cocktail") || value.includes("beverage")) return "🍹";
  if (value.includes("laundry") || value.includes("press") || value.includes("iron")) return "👕";
  if (value.includes("towel") || value.includes("linen")) return "🧻";
  if (value.includes("wifi") || value.includes("wi-fi") || value.includes("internet") || value.includes("network")) return "📶";
  if (value.includes("spa") || value.includes("massage") || value.includes("wellness")) return "🧖";
  if (value.includes("pool") || value.includes("swim")) return "🏊";
  if (value.includes("gym") || value.includes("fitness")) return "🏋️";
  if (value.includes("reception") || value.includes("desk") || value.includes("call") || value.includes("dial")) return "🛎️";
  if (value.includes("taxi") || value.includes("cab") || value.includes("transport") || value.includes("travel")) return "🚕";
  if (value.includes("maintenance") || value.includes("repair") || value.includes("fix")) return "🔧";
  if (value.includes("tea") || value.includes("coffee")) return "☕";

  return "🛎️";
};

const inferRequestType = (name = "", categoryName = "") => {
  const value = `${name} ${categoryName}`.toLowerCase();

  if (value.includes("laundry") || value.includes("press") || value.includes("iron")) {
    return "LaundryPickup";
  }

  if (value.includes("housekeeping") || value.includes("clean")) {
    return "RoomCleaning";
  }

  if (value.includes("maintenance") || value.includes("repair") || value.includes("fix")) {
    return "UrgentStaffAssistance";
  }

  if (value.includes("towel") || value.includes("linen")) {
    return "ExtraTowels";
  }

  if (value.includes("water") || value.includes("drink")) {
    return "DrinkingWater";
  }

  if (
    value.includes("food") ||
    value.includes("restaurant") ||
    value.includes("room service") ||
    value.includes("dining") ||
    value.includes("bar") ||
    value.includes("tea") ||
    value.includes("coffee")
  ) {
    return "FoodRestaurant";
  }

  if (value.includes("wifi") || value.includes("wi-fi") || value.includes("internet") || value.includes("network")) {
    return "WiFiIssue";
  }

  if (value.includes("taxi") || value.includes("cab") || value.includes("transport") || value.includes("travel")) {
    return "TaxiRequest";
  }

  if (value.includes("complaint") || value.includes("issue")) {
    return "Complaint";
  }

  return "Other";
};

const normalizeService = (service) => {
  const name = service?.name || service?.Name || "Service";
  const categoryName =
    service?.serviceCategoryName ||
    service?.ServiceCategoryName ||
    service?.category ||
    service?.Category ||
    "Hotel Services";
  const category = inferRequestType(name, categoryName);

  return {
    id: service?.id || service?.Id,
    name,
    code: service?.code || service?.Code || "",
    description: service?.description || service?.Description || "",
    icon: service?.icon || service?.Icon || inferAmenityIcon(name),
    imageUrl: service?.imageUrl || service?.ImageUrl || null,
    category,
    categoryLabel: categoryName,
    displayOrder: service?.displayOrder ?? service?.DisplayOrder ?? 0,
    estimatedDurationMinutes:
      service?.estimatedDurationMinutes ?? service?.EstimatedDurationMinutes ?? null,
    price: service?.price ?? service?.Price ?? null,
    isAvailable: service?.isAvailable ?? service?.IsAvailable ?? true,
    isActive: service?.isActive ?? service?.IsActive ?? true,
  };
};

const getRoomId = (room, session) => {
  return (
    room?.id ||
    room?.Id ||
    session?.roomId ||
    session?.RoomId ||
    session?.room?.id ||
    session?.Room?.Id ||
    ""
  );
};

const getRoomNumber = (room, session) => {
  return (
    room?.roomNumber ||
    room?.RoomNumber ||
    room?.name ||
    room?.Name ||
    session?.roomNumber ||
    session?.RoomNumber ||
    session?.roomName ||
    session?.RoomName ||
    ""
  );
};

const getGuestSessionId = (session) => {
  return session?.id || session?.Id || session?.sessionId || session?.guestSessionId || "";
};

const AmenityIcon = ({ amenity }) => {
  const [imgError, setImgError] = useState(false);
  const hasImage = Boolean(
    amenity?.imageUrl &&
      !imgError &&
      String(amenity.imageUrl).trim().toLowerCase() !== "string"
  );

  return (
    <div
      className="
        flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center
        rounded-2xl bg-[#eadfdf]/90 text-2xl sm:text-3xl
        shadow-sm transition-transform duration-200 group-hover:scale-105 overflow-hidden
      "
    >
      {hasImage ? (
        <img
          src={amenity.imageUrl}
          alt={amenity.name}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="select-none leading-none">{amenity.icon}</span>
      )}
    </div>
  );
};

const GlassAmenityCard = ({ amenity, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="
        group relative flex h-full min-h-[168px] sm:min-h-[176px] w-full flex-col justify-between
        overflow-hidden rounded-2xl sm:rounded-3xl border border-white/60
        bg-white/85 p-4 sm:p-5 text-left shadow-sm
        backdrop-blur-xl transition-all duration-200
        hover:bg-white/95 hover:border-white/90 hover:shadow-lg active:scale-[0.99]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#923536]/40
      "
    >
      <div className="flex w-full min-w-0 items-start gap-3.5 sm:gap-4">
        <AmenityIcon amenity={amenity} />

        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-1 text-base sm:text-lg font-bold text-slate-800 transition-colors group-hover:text-[#923536]">
            {amenity.name}
          </h3>

          <p className="mt-1 line-clamp-2 text-xs sm:text-sm leading-relaxed text-slate-600 whitespace-normal">
            {amenity.description || "Request this service for your room."}
          </p>
        </div>
      </div>

      <div className="mt-4 flex w-full min-w-0 items-center justify-between gap-2 border-t border-slate-200/60 pt-3">
        <span className="truncate max-w-[65%] rounded-full bg-[#923536]/10 px-2.5 py-0.5 text-xs font-semibold text-[#923536]">
          {amenity.categoryLabel}
        </span>

        <span className="inline-flex shrink-0 items-center gap-1 text-xs sm:text-sm font-semibold text-slate-700 transition-colors group-hover:text-[#923536]">
          Request
          <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </button>
  );
};

const RoomServicesPage = () => {
  const navigate = useNavigate();

  const { session, hotel, room, loading: sessionLoading } = useSession();

  const [amenities, setAmenities] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(true);
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadServices = useCallback(async () => {
    try {
      setAmenitiesLoading(true);

      let items = [];

      try {
        const response = await guestHotelServicesApi.getAll({ isAvailable: true });
        const paged = getPagedItems(response);
        if (Array.isArray(paged) && paged.length > 0) {
          items = paged
            .map(normalizeService)
            .filter((item) => item.isAvailable !== false);
        }
      } catch (err) {
        console.warn("Unable to fetch from guestHotelServicesApi:", err);
      }

      setAmenities(items);
    } catch (error) {
      toast.error(getApiMessage(error, "Could not load available services."));
      setAmenities([]);
    } finally {
      setAmenitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionLoading && !session) {
      navigate("/guest/expired", { replace: true });
      return;
    }

    if (!sessionLoading && session) {
      loadServices();
    }
  }, [sessionLoading, session, navigate, loadServices]);

  const categories = useMemo(() => {
    const list = [];
    const set = new Set();
    amenities.forEach((item) => {
      const label = item.categoryLabel || "Hotel Services";
      if (!set.has(label)) {
        set.add(label);
        list.push(label);
      }
    });
    return list;
  }, [amenities]);

  const filteredAmenities = useMemo(() => {
    if (selectedCategory === "all") return amenities;
    return amenities.filter(
      (item) => (item.categoryLabel || "Hotel Services") === selectedCategory
    );
  }, [amenities, selectedCategory]);

  const handleServiceRequest = (amenity) => {
    setSelectedAmenity(amenity);
  };

  const handleSubmitRequest = async (formData) => {
  if (!selectedAmenity) return;

  const notes =
    formData?.notes ||
    formData?.description ||
    selectedAmenity.description ||
    "";

  const quantityText = formData?.quantity
    ? `Quantity: ${formData.quantity}`
    : "";

  const description = [notes, quantityText].filter(Boolean).join("\n");

  const payload = {
    requestType: selectedAmenity.category || inferRequestType(selectedAmenity.name),
    amenityId: selectedAmenity.id,
    guestSessionId: getGuestSessionId(session),
    title: selectedAmenity.name,
    description,
  };

  try {
    setIsSubmitting(true);

    const response = await guestRequestsApi.create(payload);
    const data = getApiData(response);

    toast.success("Request submitted successfully");
    setSelectedAmenity(null);

    navigate("/guest/confirmation", {
      state: {
        request: data,
      },
    });
  } catch (error) {
    toast.error(getApiMessage(error, "Failed to submit request."));
  } finally {
    setIsSubmitting(false);
  }
};

  if (sessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{`Room Services - ${hotel?.name || "The Hotelier"}`}</title>
        <meta
          name="description"
          content="Request hotel services for your room"
        />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session} maxWidth="max-w-5xl">
        <style>
          {`
            .guest-room-services-dialog input,
            .guest-room-services-dialog textarea,
            .guest-room-services-dialog select {
              background-color: rgba(255, 255, 255, 0.85) !important;
              border-color: rgba(226, 232, 240, 0.8) !important;
            }
          `}
        </style>

        <div className="guest-room-services-glass mx-auto max-w-5xl space-y-6">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-white text-balance">
              Room Services
            </h1>

            <p className="text-sm text-white/75">
              Select an amenity or service to make a request for your room.
            </p>
          </div>

          {categories.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <Button
                type="button"
                variant={selectedCategory === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory("all")}
                className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold shadow-none transition-all ${
                  selectedCategory === "all"
                    ? "bg-[#923536] text-white hover:bg-[#7b2c2d]"
                    : "border-white/50 bg-white/80 text-slate-700 hover:bg-white"
                }`}
              >
                All Services ({amenities.length})
              </Button>
              {categories.map((cat) => {
                const count = amenities.filter(
                  (a) => (a.categoryLabel || "Hotel Services") === cat
                ).length;
                const isSelected = selectedCategory === cat;
                return (
                  <Button
                    key={cat}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    className={`shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold shadow-none transition-all ${
                      isSelected
                        ? "bg-[#923536] text-white hover:bg-[#7b2c2d]"
                        : "border-white/50 bg-white/80 text-slate-700 hover:bg-white"
                    }`}
                  >
                    {cat} ({count})
                  </Button>
                );
              })}
            </div>
          )}

          {amenitiesLoading ? (
            <div
              className="
                flex min-h-[260px] items-center justify-center
                rounded-3xl border border-white/40
                bg-white/75 backdrop-blur-md shadow-sm
              "
            >
              <div className="text-center">
                <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#923536]" />

                <p className="mt-4 text-sm font-medium text-slate-600">
                  Loading available services...
                </p>
              </div>
            </div>
          ) : filteredAmenities.length === 0 ? (
            <div
              className="
                rounded-3xl border border-white/40
                bg-white/75 p-10 text-center
                backdrop-blur-md shadow-sm
              "
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#eadfdf]/90 text-[#923536]">
                <Info className="h-8 w-8" />
              </div>

              <h3 className="text-xl font-bold text-slate-800">
                No services available
              </h3>

              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                There are currently no active services configured for this
                hotel. Please contact the front desk for assistance.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-5 items-stretch">
              {filteredAmenities.map((amenity) => (
                <GlassAmenityCard
                  key={amenity.id}
                  amenity={amenity}
                  onClick={() => handleServiceRequest(amenity)}
                />
              ))}
            </div>
          )}
        </div>

        <Dialog
          open={!!selectedAmenity}
          onOpenChange={(open) => {
            if (!open) setSelectedAmenity(null);
          }}
        >
          <DialogContent
            className="
              guest-room-services-dialog
              border border-white/50
              bg-white/95 backdrop-blur-xl
              text-slate-800 shadow-2xl
              sm:max-w-[480px] rounded-3xl
            "
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-xl font-bold text-slate-800">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#eadfdf]/90 text-2xl">
                  {selectedAmenity?.icon || <ConciergeBell className="h-6 w-6" />}
                </span>

                <span className="truncate">Request {selectedAmenity?.name}</span>
              </DialogTitle>
            </DialogHeader>

            {selectedAmenity && (
              <ServiceRequestForm
                amenity={selectedAmenity}
                onSubmit={handleSubmitRequest}
                onCancel={() => setSelectedAmenity(null)}
                loading={isSubmitting}
              />
            )}
          </DialogContent>
        </Dialog>
      </GuestLayout>
    </>
  );
};

export default RoomServicesPage;
